import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const CancelledBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch data from the API with pagination
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);

        // Calculate offset based on current page
        const offset = (currentPage - 1) * itemsPerPage;

        // Fetch paginated data from API
        const response = await fetch(
          `${BQ_BASE_URL}/bq/api/cancelled-bookings?limit=${itemsPerPage}&offset=${offset}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Also fetch total count for pagination
        const countResponse = await fetch(`${BQ_BASE_URL}/bq/api/cancelled-bookings/count`);

        if (countResponse.ok) {
          const countData = await countResponse.json();
          setTotalCount(countData.count || 0);
        }

        // Map API response to frontend expected format
        if (Array.isArray(data)) {
          const mappedBookings = data.map((booking) => ({
            booking_id: booking.bookingid,
            order_id: booking.orderid,
            guest_name: booking.guest
              ? `${booking.guest.firstname || ''} ${booking.guest.lastname || ''}`.trim()
              : 'N/A',
            phonenumber: booking.guest?.phonenumber || 'N/A',
            room_number: booking.room?.roomnumber || null, // Send null if no room assigned
            room_type: booking.room?.roomtypename || booking.roomtypename || 'N/A',
            checkin_date: booking.checkindate || 'N/A',
            checkout_date: booking.checkoutdate || 'N/A',
            booking_status: booking.booking_status,
            cancellation_reason: booking.cancellation_reason || 'No reason provided',
            // Keep original data for reference if needed
            original: booking,
          }));
          setBookings(mappedBookings);
        } else {
          console.warn('Unexpected API response format:', data);
          setBookings([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [currentPage, itemsPerPage]);

  // Filter and search logic
  const filteredBookings = Array.isArray(bookings)
    ? bookings.filter((booking) => {
        const searchTerm = searchQuery.toLowerCase();
        const matchesSearchQuery =
          booking.guest_name?.toLowerCase().includes(searchTerm) ||
          booking.order_id?.toLowerCase().includes(searchTerm) ||
          (booking.cancellation_reason || '').toLowerCase().includes(searchTerm) ||
          (booking.room_type || '').toLowerCase().includes(searchTerm);

        const matchesStatus = filterStatus === 'all' || booking.booking_status === filterStatus;

        return matchesSearchQuery && matchesStatus;
      })
    : [];

  // Calculate total pages based on totalCount from API
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Generate page numbers with ellipsis logic
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        endPage = maxPagesToShow;
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (startPage > 1) {
        pageNumbers.unshift('...');
      }
      if (endPage < totalPages) {
        pageNumbers.push('...');
      }
    }

    return pageNumbers.map((number, index) =>
      number === '...' ? (
        <span key={index} className="px-3 py-1 text-gray-600">
          {number}
        </span>
      ) : (
        <button
          key={index}
          onClick={() => paginate(number)}
          className={`px-3 py-1 mx-1 rounded-full ${
            currentPage === number
              ? 'bg-black text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {number}
        </button>
      )
    );
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading cancelled reservations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex flex-col">
        <NavLink
          to="/dashboard/core-modules/property-management-system/front-desk-operations"
          className="flex items-center text-gray-400 hover:text-black mb-4 self-start"
        >
          <FaArrowLeft className="mr-3 text-md" />
          <span className="text-lg">Back to Front Desk Operations</span>
        </NavLink>

        <div className="text-center max-w-md mx-auto">
          {/* 404 Icon/Image */}
          <div className="mb-6">
            <div className="text-9xl font-bold text-gray-300">404</div>
            <div className="text-2xl font-bold text-gray-600 mt-4">Page Not Found</div>
          </div>

          {/* Error Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-center mb-3">
              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-2">
                <span className="text-white text-sm font-bold">!</span>
              </div>
              <h2 className="text-xl font-bold text-red-800">Error Loading Data</h2>
            </div>
            <p className="text-red-700 mb-4">{error}</p>
            <p className="text-sm text-red-600">
              The cancelled reservation data could not be loaded. Please check your connection and
              try again.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-semibold"
            >
              Try Again
            </button>
            <NavLink
              to="/dashboard/core-modules/property-management-system/front-desk-operations"
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-center"
            >
              Return to Dashboard
            </NavLink>
          </div>

          {/* Additional Help */}
          <div className="mt-8 text-sm text-gray-500">
            <p>If the problem persists, please contact support.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <NavLink
        to="/dashboard/core-modules/property-management-system/front-desk-operations"
        className="flex items-center text-gray-400 hover:text-black mb-4"
      >
        <FaArrowLeft className="mr-3 text-md" />
        <span className="text-lg">Back to Front Desk Operations</span>
      </NavLink>

      {/* Header */}
      <header className="text-center mb-8 mt-4">
        <h1 className="text-3xl font-bold text-center text-black">Cancelled Bookings</h1>
        <p className="text-sm font-poppins text-center mb-1 text-gray-600">
          View and manage all cancelled reservations. Search by name, reservation number.
        </p>
        <hr className="border-gray-400 mt-1 w-1/3 mx-auto" />
      </header>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <input
          type="text"
          placeholder="Search by name, reservation number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="p-3 w-full sm:w-1/3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-4 text-center text-sm font-semibold text-black uppercase tracking-wider">
                  Reservation Number
                </th>
                <th className="px-2 py-4 text-left text-sm font-semibold text-black uppercase tracking-wider">
                  Guest Name
                </th>
                <th className="px-2 py-4 text-left text-sm font-semibold text-black uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="px-2 py-4 text-left text-sm font-semibold text-black uppercase tracking-wider">
                  Room Number
                </th>
                <th className="px-2 py-4 text-left text-sm font-semibold text-black uppercase tracking-wider">
                  Room Type
                </th>
                <th className="px-2 py-4 text-left text-sm font-semibold text-black uppercase tracking-wider">
                  Check-In Date
                </th>
                <th className="px-2 py-4 text-left text-sm font-semibold text-black uppercase tracking-wider">
                  Check-Out Date
                </th>
                <th className="px-2 py-4 text-left text-sm font-semibold text-black uppercase tracking-wider">
                  Status
                </th>
                <th className="px-2 py-4 text-left text-sm font-semibold text-black uppercase tracking-wider">
                  Cancellation Reason
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredBookings.length > 0 ? (
                filteredBookings.map((booking, index) => (
                  <tr
                    key={booking.order_id || index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-2 py-4 text-center whitespace-nowrap text-sm text-gray-900 font-mono">
                      {booking.order_id}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.guest_name}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.phonenumber}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.room_number || 'N/A'}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.room_type}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.checkin_date}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.checkout_date}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        {booking.booking_status}
                      </span>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                      {booking.cancellation_reason}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-gray-500">
                    {bookings.length === 0
                      ? 'No cancelled bookings available.'
                      : 'No bookings match your search criteria.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-black text-white rounded-l disabled:opacity-50 hover:bg-gray-800 transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center mx-2">{renderPageNumbers()}</div>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-6 py-2 bg-black text-white rounded-r disabled:opacity-50 hover:bg-gray-800 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CancelledBookings;
